import {
  type EnvironmentSettingsWithApi,
  useAuthenticationContext,
  useFetchAppSettings,
} from '@festina-life/authentication'
import {
  getObjectEntries,
  getObjectKeys,
  getObjectValues,
  type Optional,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  FixedWidthInputs,
  Select,
  Switch,
  TextInput,
} from '@festina-life/ui/forms'
import { useLocalStorageShouldTranslate } from '@festina-life/ui/hooks'
import { spacingStyles } from '@festina-life/ui/libs'
import { Modal, Stack } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useFormik } from 'formik'
import { type ChangeEvent, useEffect, useId, useState } from 'react'

import { useSettingsContext } from '../settingsProvider/settingsContext'

// Regex to split e.g. http://localhost:7744 into ["http://", "localhost", ":7744"]
const API_REGEX = /(^.*:\/\/)(.*?)(\/|:)/

type Props = {
  value: Optional<EnvironmentSettingsWithApi>
  onChange: (nextSettings: EnvironmentSettingsWithApi) => void
  onCloseModal: () => void
  isModalOpen: boolean
}

export const Settings = ({
  value,
  onChange,
  onCloseModal,
  isModalOpen,
}: Props) => {
  const [apiOptions, setApiOptions] = useState<string[]>([])
  const settingsId = useId()
  const { data: appSettings } = useFetchAppSettings()
  const { changeDebugMode, isInDebugMode } = useSettingsContext()

  const {
    shouldTranslateTexts,
    shouldTranslateEnums,
    onChangeShouldTranslateTexts,
    onChangeShouldTranslateEnums,
  } = useLocalStorageShouldTranslate()
  const {
    shouldValidateAuthenticationHooks,
    onChangeShouldValidateAuthenticationHooks,
  } = useAuthenticationContext()
  const [internalDebugMode, setInternalDebugMode] = useState(isInDebugMode)
  const environments = appSettings?.environments

  if (!environments) {
    throw new Error('Environment file has not been set up correct')
  }

  const firstEnvironment = getObjectValues(appSettings.environments)[0]
  const environmentApiKeys = getObjectKeys(firstEnvironment).filter((envKey) =>
    envKey.includes('API_ENDPOINT'),
  )

  useEffect(() => {
    if (environments) {
      const options = Object.keys(environments)
      setApiOptions([...options, 'customize'])
    }
  }, [environments])

  const formik = useFormik<
    Partial<EnvironmentSettingsWithApi & { customCommonApiBase: string }>
  >({
    initialValues: {
      ...value,
      api: value?.api ?? 'customize',
      customCommonApiBase: '',
    },
    onSubmit: (values) => {
      const finalValues = { ...value, ...values }

      if (values.api === '') {
        localStorage.removeItem('appSettings')
      } else {
        onChange(finalValues as EnvironmentSettingsWithApi)
      }
      onCloseModal()
    },
  })

  const handleApiChange = (nextValue: string | undefined) => {
    const { api, customCommonApiBase, ...environmentValues } = formik.values
    const nextValues = getObjectKeys(environmentValues).reduce(
      (acc, key) => {
        if (
          nextValue &&
          environments &&
          environments[nextValue] &&
          environments[nextValue][key]
        ) {
          return { ...acc, [key]: environments[nextValue][key] }
        } else {
          return { ...acc, [key]: environmentValues[key] }
        }
      },
      { api: nextValue, customCommonApiBase },
    )

    void formik.setValues(nextValues)
  }

  const handleCustomCommonApiBaseFocus = () => {
    const lifeApi = formik.values.REACT_APP_LIFE_API_ENDPOINT

    if (lifeApi) {
      const match = lifeApi.match(API_REGEX)

      if (match) {
        void formik.setValues({
          ...formik.values,
          customCommonApiBase: match[2],
        })
      }
    }
  }

  const handleCustomCommonApiBaseChange = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue = e.target.value
    const { api, customCommonApiBase, ...environmentValues } = formik.values

    const nextValues = {
      ...Object.fromEntries(
        getObjectEntries(environmentValues).map(([key, value]) =>
          typeof value === 'string' &&
          value.includes('http://') && // this ensures we don't edit e.g. MSGRAPH_API_ENDPOINT
          environmentApiKeys.includes(key)
            ? [key, value.replace(API_REGEX, `$1${nextValue}$3`)]
            : [key, value],
        ),
      ),
      customCommonApiBase: nextValue,
      api: 'customize',
    }

    void formik.setValues(nextValues)
  }

  return (
    <Modal onClose={onCloseModal} isOpen={isModalOpen}>
      <Modal.Title title="Developer settings" />
      <Modal.Body innerClassName="grid grid-cols-2 gap-4 py-0">
        <FixedWidthInputs>
          <form
            onSubmit={formik.handleSubmit}
            id={settingsId}
            className={cx(
              spacingStyles({ gap: 'spacious', padding: 'none' }),
              'flex flex-col',
            )}
          >
            <Select
              wrapperProps={{
                label: 'API',
              }}
              options={apiOptions}
              {...formik.getFieldProps('api')}
              onChange={handleApiChange}
              shouldShowClearButton={false}
              sort={false}
            />
            {!getObjectKeys(environments).includes(formik.values.api ?? '') && (
              <TextInput
                wrapperProps={{
                  label: 'Custom API base:',
                  htmlFor: 'customCommonApiBase',
                }}
                id="customCommonApiBase"
                {...formik.getFieldProps('customCommonApiBase')}
                onChange={handleCustomCommonApiBaseChange}
                onFocus={handleCustomCommonApiBaseFocus}
                autoFocus
              />
            )}
            {environmentApiKeys.map((key) => (
              <TextInput
                key={key}
                wrapperProps={{
                  label: `${key.replace('REACT_APP_', '').replace('_API_ENDPOINT', '')}:`,
                  htmlFor: key,
                }}
                disabled={formik.values.api !== 'customize'}
                id={key}
                {...formik.getFieldProps(key)}
              />
            ))}
          </form>
          <Stack className="flex-col" gap="spacious">
            <Switch
              wrapperProps={{ label: 'Translate text' }}
              checked={shouldTranslateTexts}
              onChange={onChangeShouldTranslateTexts}
            />
            <Switch
              wrapperProps={{ label: 'Translate enums' }}
              checked={shouldTranslateEnums}
              onChange={onChangeShouldTranslateEnums}
            />
            <Switch
              wrapperProps={{ label: 'Strict authentication' }}
              checked={shouldValidateAuthenticationHooks}
              onChange={(x) => {
                onChangeShouldValidateAuthenticationHooks(x)
              }}
            />
            <Switch
              wrapperProps={{
                label: 'Debug mode',
                description:
                  'Note that debug mode will add an extra icon next to parameters when hovered by the mouse. Click the icon to see extra information regarding some fields',
              }}
              checked={internalDebugMode}
              onChange={(x) => {
                changeDebugMode(x)
                setInternalDebugMode(x)
              }}
            />
            {internalDebugMode !== isInDebugMode && (
              <Button onClick={() => window.location.reload()}>
                Refresh to update
              </Button>
            )}
          </Stack>
        </FixedWidthInputs>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            localStorage.removeItem('appSettings')
            window.location.reload()
          }}
        >
          Reset app settings
        </Button>
        <Button variant="primary" type="submit" form={settingsId}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
